<script setup lang="ts">
  import { useSearch, useRecentSearch } from '../../utils/search-providers'
  import {
    searchContextItems,
    type SearchContextType
  } from '../../utils/global-search-utils'
  import GlobalSearchInput from './global-search-input.vue'
  import { searchContextTypesMapping } from '../../utils/global-search-utils'
  import { ArrowDown, ArrowUp } from 'lucide-vue-next'
  import type { RecentSearch, SearchResult } from '../../utils/search-types'
  import { computed, useTemplateRef, watchEffect } from 'vue'
  import RecentSearchItem from '../recent-search/recent-search-item.vue'
  import GlobalSearchContextItem from './global-search-context-item.vue'
  import type { GlobalSearchContextItemProps } from './global-search-context-item.vue'
  import SearchResultItemComponent, {
    type SearchResultItemProps
  } from '../search-result/search-result-item.vue'
  import { useRouter } from '@gat-ship/lib/router'
  import { useGlobalSearchDialog } from './global-search-provider.vue'
  import SearchResultHeader from '../search-result/search-result-header.vue'
  import HelpDialogGlobalSearch from '../help-dialogs/help-dialog-global-search.vue'
  import SearchDialogItems from '../search-items/search-dialog-items.vue'

  const { closeSearchCommand } = useGlobalSearchDialog()

  const {
    searchInputValue: searchInput,
    isSearchInputEmpty: isSearchEmpty,
    callSearch,
    setSelectedSearchContext,
    isSearching,
    selectedSearchContext,
    searchResults,
    isEmptySearchResults,
    allowedSearchContextsMetadata,
    searchInputRef
  } = useSearch()

  const { recentSearchItems } = useRecentSearch()

  const globalSearchDialogContentRef = useTemplateRef(
    'globalSearchDialogContentRef'
  )

  interface SearchDialogItem<T> {
    id: string
    title: string
    focusable: boolean
    props?: T
    component?: Component
    selected?: (item: T) => void
  }

  function getRecentSearchDialogItems(
    items: RecentSearch[]
  ): SearchDialogItem<RecentSearch>[] {
    let result: SearchDialogItem<RecentSearch>[] = items.map((item) => ({
      id: item.searchString + item.context,
      title: item.searchString,
      focusable: true,
      props: item,
      component: RecentSearchItem,
      selected: (item) => {
        callSearch(item.searchString, item.context)
        searchInputRef?.value?.focus()
      }
    }))
    if (selectedSearchContext.value !== 'global') {
      result = result.filter(
        (item) => item.props?.context === selectedSearchContext.value
      )
    }
    return result.slice(0, 5)
  }

  function getContextSearchDialogItems(): SearchDialogItem<GlobalSearchContextItemProps>[] {
    const result: SearchDialogItem<GlobalSearchContextItemProps>[] = []
    Object.entries(allowedSearchContextsMetadata.value).forEach(
      ([key, value]) => {
        if (
          !searchInput.value &&
          selectedSearchContext.value === searchContextItems.global &&
          key === searchContextItems.global
        ) {
          return
        }
        result.push({
          id: key,
          title: value.title,
          focusable: true,
          props: { context: key as SearchContextType },
          component: GlobalSearchContextItem,
          selected: (item) => {
            if (item.context === searchContextItems.global) {
              callSearch(searchInput.value, item.context)
            } else {
              setSelectedSearchContext(item.context)
            }
            searchInputRef?.value?.focus()
          }
        })
      }
    )
    return result
  }

  const router = useRouter()

  function getGlobalContextSearchDialogItems(
    searchResults: SearchResult[]
  ): SearchDialogItem<SearchResultItemProps>[] {
    const result: SearchDialogItem<any>[] = []
    const maxItemsPerContext = 3
    const groupedByContext = searchResults.reduce(
      (acc, item) => {
        acc[item.contextType] = acc[item.contextType] || []
        acc[item.contextType].push(item)
        return acc
      },
      {} as Record<SearchContextType, SearchResult[]>
    )

    Object.entries(groupedByContext).forEach(([key, items]) => {
      const context = key as SearchContextType
      if (items.length > 0) {
        result.push({
          id: context,
          title: searchContextTypesMapping[context as SearchContextType].title,
          focusable: false,
          component: SearchResultHeader,
          props: {
            title:
              searchContextTypesMapping[context as SearchContextType].title,
            textRight:
              items.length > maxItemsPerContext
                ? `${items.length} ${'result'.gs_toPlural(items.length, 's')}`
                : `${items.length} ${'result'.gs_toPlural(items.length, 's')}`
          }
        })
        items.slice(0, maxItemsPerContext).forEach((item) => {
          result.push({
            id: item.id,
            title: item.title,
            focusable: true,
            props: item,
            component: SearchResultItemComponent,
            selected: (item) => {
              item.actions.selected(router)
              closeSearchCommand()
            }
          })
        })
        if (items.length >= maxItemsPerContext) {
          result.push({
            id: `${context}-more`,
            title: `+${items.length - maxItemsPerContext} more`,
            focusable: true,
            props: {
              title: `Show all ${items.length} results`,
              subTitle: `${searchContextTypesMapping[context as SearchContextType].title}`,
              icon: 'Search',
              contextType: context as SearchContextType,
              actions: {
                selected: () => {
                  callSearch(searchInput.value, context as SearchContextType)
                  searchInputRef?.value?.focus()
                }
              },
              id: `${context}-more`
            },
            component: SearchResultItemComponent,
            selected: (item) => {
              callSearch(searchInput.value, context as SearchContextType)
              searchInputRef?.value?.focus()
            }
          })
        }
      }
    })
    return result
  }

  function getSearchResultsDialogItems():
    | SearchDialogItem<SearchResultItemProps>[]
    | undefined {
    const _searchResults = searchResults.value
    if (!_searchResults) {
      return undefined
    }
    if (selectedSearchContext.value === searchContextItems.global) {
      return getGlobalContextSearchDialogItems(_searchResults)
    }
    return _searchResults.map((result) => ({
      id: result.id,
      title: result.title,
      focusable: true,
      props: result,
      component: SearchResultItemComponent,
      selected: (result) => {
        result.actions.selected(router)
        closeSearchCommand()
      }
    }))
  }

  const searchDialogItems = computed((): SearchDialogItem<any>[] => {
    const result: SearchDialogItem<any>[] = []

    const isGlobalContext =
      selectedSearchContext.value === searchContextItems.global
    const _recentSearchItems = getRecentSearchDialogItems(
      recentSearchItems.value
    )
    if (
      isSearchEmpty.value &&
      isGlobalContext &&
      _recentSearchItems.length > 0
    ) {
      // Recent search items
      result.push({
        id: 'recent-search-items-header',
        title: 'Recent searches',
        focusable: false
      })
      result.push(..._recentSearchItems)
    }
    if (isGlobalContext && !searchResults.value && !isSearching.value) {
      // Context searches
      result.push({
        id: 'context-searches-header',
        title: 'Context searches',
        focusable: false
      })
      result.push(...getContextSearchDialogItems())
    }
    const searchResultsDialogItems = getSearchResultsDialogItems()
    if (searchResultsDialogItems) {
      /* result.push({
        id: 'search-results-header',
        title: `${searchResults.value?.length ?? 0} results found (${searchContextTypesMapping[selectedSearchContext.value].title})`,
        focusable: false,
        component: SearchResultHeader,
        props: {
          searchResultsCount: searchResults.value?.length ?? 0
        }
      }) */

      result.push(...searchResultsDialogItems)
    }
    return result
  })

  const searchItemsRef = useTemplateRef('searchItemsRef')

  watchEffect(() => {
    isSearching.value
    searchInput.value
    searchItemsRef.value?.keyboardNavigation.focusFirstItem()
  })
</script>

<template>
  <GlobalSearchInput />
  <div
    ref="globalSearchDialogContentRef"
    class="divide-border relative divide-y border-x"
  >
    <SearchDialogItems
      ref="searchItemsRef"
      :content-container-ref="globalSearchDialogContentRef"
      :items="searchDialogItems"
    >
    </SearchDialogItems>
    <template v-if="isSearching">
      <div class="flex flex-col gap-2 p-4">
        <div class="text-xs font-medium">Searching...</div>
      </div>
    </template>
    <template
      v-else-if="!isSearchEmpty && isEmptySearchResults && !isSearching"
    >
      <div class="flex flex-col gap-2 p-4">
        <div class="text-xs font-medium">
          No results found for "{{ searchInput }}" in
          {{ searchContextTypesMapping[selectedSearchContext].title }}
        </div>
      </div>
    </template>
    <div class="flex flex-col gap-2 bg-gray-50 py-2 pl-4 pr-2">
      <div class="text-soft flex items-center gap-4 text-xs">
        <div class="flex flex-col items-center justify-center gap-1">
          <gat-keyboard-shortcuts class="w-fit">
            <ArrowDown class="size-4" />
          </gat-keyboard-shortcuts>
          <div class="text-xs text-gray-400">down</div>
        </div>
        <div class="flex flex-col items-center justify-center gap-1">
          <gat-keyboard-shortcuts class="w-fit">
            <ArrowUp class="size-4" />
          </gat-keyboard-shortcuts>
          <div class="text-xs text-gray-400">up</div>
        </div>
        <div class="flex w-fit flex-col items-center justify-center gap-1">
          <gat-keyboard-shortcuts class="w-fit"> esc </gat-keyboard-shortcuts>
          <div class="text-xs text-gray-400">close</div>
        </div>
        <HelpDialogGlobalSearch>
          <template #trigger>
            <div
              class="hover:bg-accent hover:text-foreground ml-auto flex w-fit cursor-pointer select-none flex-col items-center justify-center gap-1 rounded-md p-2 px-4 transition-colors"
            >
              <gat-icon icon="CircleQuestionMark" class="size-4" />
              <div class="text-xs">help</div>
            </div>
          </template>
        </HelpDialogGlobalSearch>
      </div>
    </div>
  </div>
</template>
